// Apolo IDE — Durable spec model (renderer, sprint hgmq-finish, P7-T03/T05).
// @arch:pure-engine
//
// Renderer-side pure mirror of apolo-api/src/lib/specStore.js (kept small +
// in sync by tests). Builds a Kiro 3-file spec from an approved plan and
// derives the read-model the Specs surface renders. PURE — the `.apolo/specs/`
// disk write is I/O at the edge (store action via window.apolo.file.write).
//
// Lifecycle (reconciled with "spec is ephemeral"): ephemeral in the Glass Box
// during the turn → persisted to .apolo/specs/<slug>/{requirements,design,
// tasks}.md ONLY on human approval (the approval gate is the write boundary).

const TASK_LINE_RE = /^\s*[-*]\s*\[( |x|X)\]\s*([\w.-]+)[.)]\s*(.+?)\s*$/;

// FASE-12 E5-T05 — hash curto determinístico (djb2 → base36) do slug completo, pra
// preservar unicidade quando o cap corta dois features que compartilham o prefixo.
function shortHash(s) {
  let h = 5381;
  for (let i = 0; i < s.length; i += 1) h = (((h << 5) + h) ^ s.charCodeAt(i)) >>> 0;
  return h.toString(36);
}

export function specPaths(feature) {
  const raw = String(feature ?? 'feature').trim().toLowerCase()
    .replace(/[^a-z0-9._-]+/g, '-').replace(/^-+|-+$/g, '') || 'feature';
  // E5-T05 — cap do slug: features longos (ex.: a task inteira como feature) geravam
  // dirs de 200+ chars poluindo .apolo/specs/. 56 chars + hash do slug completo mantém
  // legível E único. .apolo/specs/ é ignorado no git (artefato efêmero por aprovação).
  const slug = raw.length > 64 ? `${raw.slice(0, 56).replace(/-+$/, '')}-${shortHash(raw)}` : raw;
  const dir = `.apolo/specs/${slug}`;
  return { slug, dir, requirements: `${dir}/requirements.md`, design: `${dir}/design.md`, tasks: `${dir}/tasks.md` };
}

/** EARS clause count for the Glass Box meter. Pure. */
export function earsClauses(requirementsMd) {
  const re = /\bWHEN\s+.+?\s+THE\s+SYSTEM\s+SHALL\s+.+/i;
  return String(requirementsMd ?? '').split(/\r?\n/).filter((l) => re.test(l)).length;
}

/** tasks.md checklist → Task[] (taskGraph shape). Pure. Mirrors specStore. */
export function deriveTasksFromSpec(tasksMd) {
  const out = [];
  for (const line of String(tasksMd ?? '').split(/\r?\n/)) {
    const m = TASK_LINE_RE.exec(line);
    if (!m) continue;
    const id = m[2].trim();
    let rest = m[3].trim();
    let acceptance;
    const acc = rest.match(/[—-]\s*acceptance:\s*(.+)$/i);
    if (acc) { acceptance = acc[1].trim(); rest = rest.slice(0, acc.index).trim(); }
    let deps = [];
    const dep = rest.match(/\(deps:\s*([^)]*)\)/i);
    if (dep) { deps = dep[1].split(',').map((d) => d.trim()).filter(Boolean); rest = rest.replace(dep[0], '').trim(); }
    const name = rest.trim();
    if (!id || !name) continue;
    const t = { id, name, deps, status: m[1].toLowerCase() === 'x' ? 'green' : 'pending' };
    if (acceptance) t.acceptance = acceptance;
    out.push(t);
  }
  return out;
}

/**
 * Build a durable spec object from an approved plan (`present_plan` shape:
 * { summary, steps, questions?, tasks? }). Requirements come from the
 * clarifying questions/answers (EARS where derivable) + summary; design from a
 * stub; tasks from plan.tasks (or steps). Pure.
 */
export function buildSpecFromPlan(plan, { feature, answers = [] } = {}) {
  const summary = String(plan?.summary ?? '').trim();
  const feat = String(feature ?? summary.slice(0, 48) ?? 'feature').trim() || 'feature';

  // Requirements: EARS-ish lines from answers + a fallback from the summary.
  const reqLines = ['# Requirements (EARS)', ''];
  if (summary) reqLines.push(`Objetivo: ${summary}`, '');
  for (const a of (Array.isArray(answers) ? answers : [])) {
    if (a?.value) reqLines.push(`- WHEN ${a.questionId || 'a condição é atendida'} THE SYSTEM SHALL ${a.value}`);
  }
  if (reqLines.length === (summary ? 4 : 2)) {
    reqLines.push('- WHEN o usuário aprova o plano THE SYSTEM SHALL executar as tasks na ordem do DAG');
  }

  // Design stub.
  const design = ['# Design', '', summary || 'Arquitetura derivada do plano aprovado.', '',
    'Sequência: aprovação humana → execução task-by-task (ready-set do DAG) → honest-green por task.'].join('\n');

  // Tasks: from plan.tasks (DAG) when present, else from steps.
  const tasksArr = Array.isArray(plan?.tasks) && plan.tasks.length
    ? plan.tasks
    : (Array.isArray(plan?.steps) ? plan.steps.map((s, i) => ({
      id: String(s?.n ?? i + 1), name: String(s?.action ?? s ?? `Step ${i + 1}`), deps: i === 0 ? [] : [String(i)],
    })) : []);
  const taskLines = ['# Tasks', ''];
  for (const t of tasksArr) {
    const deps = (t.deps?.length ? ` (deps: ${t.deps.join(', ')})` : '');
    const acc = (t.acceptance ? ` — acceptance: ${t.acceptance}` : '');
    taskLines.push(`- [${t.status === 'green' ? 'x' : ' '}] ${t.id}. ${t.name}${deps}${acc}`);
  }

  const requirements = reqLines.join('\n');
  const tasks = taskLines.join('\n');
  return {
    feature: feat,
    requirements: { md: requirements },
    design: { md: design },
    tasks: { md: tasks, derived: deriveTasksFromSpec(tasks) },
    stale: { design: false, tasks: false },
    approvedAt: null, // stamped by the store on persist (no Date in pure code)
  };
}

/** Serialize to the 3 markdown files. Pure. */
export function serializeSpec(spec) {
  return {
    requirements: spec?.requirements?.md ?? '',
    design: spec?.design?.md ?? '',
    tasks: spec?.tasks?.md ?? '',
  };
}

/** Glass Box read-model for the Specs surface. Pure. */
export function specGlassBoxView(spec) {
  return {
    feature: spec?.feature ?? null,
    earsClauses: earsClauses(spec?.requirements?.md),
    taskCount: spec?.tasks?.derived?.length ?? deriveTasksFromSpec(spec?.tasks?.md).length,
    stale: { design: !!spec?.stale?.design, tasks: !!spec?.stale?.tasks },
    durable: !!spec?.approvedAt,
  };
}
